import type * as Schema from 'owa-graph-schema';
import type UnifiedGroupDetails from 'owa-service/lib/contract/UnifiedGroupDetails';
import type UserUnifiedGroupRelationship from 'owa-service/src/contract/UserUnifiedGroupRelationship';
import UnifiedGroupSKUType from 'owa-service/src/contract/UnifiedGroupSKUType';
import UnifiedGroupAccessType from 'owa-service/src/contract/UnifiedGroupAccessType';
import UnifiedGroupExpiryStatus from 'owa-service/src/contract/UnifiedGroupExpiryStatus';
import MailboxAssociationSubscriptionType from 'owa-service/src/contract/UnifiedGroupSubscriptionType';
import type RetentionPolicyTag from 'owa-service/src/contract/RetentionPolicyTag';
import RetentionActionType from 'owa-service/src/contract/RetentionActionType';
import ElcFolderType from 'owa-service/src/contract/ElcFolderType';
import type UnifiedGroupMailboxSettings from 'owa-service/src/contract/UnifiedGroupMailboxSettings';
import type UnifiedGroupDetailsAdditionalProperties from 'owa-service/src/contract/UnifiedGroupDetailsAdditionalProperties';
import Dl2GroupMailCopyStatus from 'owa-service/src/contract/Dl2GroupMailCopyStatus';

function getUserGroupRelationship(
    gqlUserGroupRelationship: Schema.UserUnifiedGroupRelationship
): UserUnifiedGroupRelationship {
    let subscriptionTypeJson = undefined;

    switch (gqlUserGroupRelationship.SubscriptionType) {
        case 'None':
            subscriptionTypeJson = MailboxAssociationSubscriptionType.None;
            break;
        case 'Message':
            subscriptionTypeJson = MailboxAssociationSubscriptionType.Message;
            break;
        case 'Calendar':
            subscriptionTypeJson = MailboxAssociationSubscriptionType.Calendar;
            break;
        case 'All':
            subscriptionTypeJson = MailboxAssociationSubscriptionType.All;
            break;
    }

    return {
        ...gqlUserGroupRelationship,
        SubscriptionType: subscriptionTypeJson,
    } as UserUnifiedGroupRelationship;
}

function mapUnifiedGroupSKU(
    gqlUnifiedGroupSKU: Schema.UnifiedGroupSkuType
): UnifiedGroupSKUType | undefined {
    let unifiedGroupSKUJson = undefined;

    switch (gqlUnifiedGroupSKU) {
        case 'Default':
            unifiedGroupSKUJson = UnifiedGroupSKUType.Default;
            break;
        case 'HomeRoom':
            unifiedGroupSKUJson = UnifiedGroupSKUType.HomeRoom;
            break;
        case 'Yammer':
            unifiedGroupSKUJson = UnifiedGroupSKUType.Yammer;
            break;
        case 'SkypeConsumer':
            unifiedGroupSKUJson = UnifiedGroupSKUType.SkypeConsumer;
            break;
        case 'PartialCreate':
            unifiedGroupSKUJson = UnifiedGroupSKUType.PartialCreate;
            break;
        case 'PartialPostCreate':
            unifiedGroupSKUJson = UnifiedGroupSKUType.PartialPostCreate;
            break;
        case 'TeamsChannel':
            unifiedGroupSKUJson = UnifiedGroupSKUType.TeamsChannel;
            break;
    }

    return unifiedGroupSKUJson as UnifiedGroupSKUType;
}

function mapAccessType(
    gqlAccessType: Schema.UnifiedGroupAccessType
): UnifiedGroupAccessType | undefined {
    let accessTypeJson = undefined;

    switch (gqlAccessType) {
        case 'None':
            accessTypeJson = UnifiedGroupAccessType.None;
            break;
        case 'Private':
            accessTypeJson = UnifiedGroupAccessType.Private;
            break;
        case 'Secret':
            accessTypeJson = UnifiedGroupAccessType.Secret;
            break;
        case 'Public':
            accessTypeJson = UnifiedGroupAccessType.Public;
            break;
    }

    return accessTypeJson as UnifiedGroupAccessType;
}

function mapExpiryStatus(
    gqlExpiryStatus: Schema.UnifiedGroupExpiryStatus
): UnifiedGroupExpiryStatus | undefined {
    let expiryStatusJson = undefined;

    switch (gqlExpiryStatus) {
        case 'NotExpiring':
            expiryStatusJson = UnifiedGroupExpiryStatus.NotExpiring;
            break;
        case 'Expiring':
            expiryStatusJson = UnifiedGroupExpiryStatus.Expiring;
            break;
        case 'Expired':
            expiryStatusJson = UnifiedGroupExpiryStatus.Expired;
            break;
        case 'NotEligible':
            expiryStatusJson = UnifiedGroupExpiryStatus.NotEligible;
            break;
        default:
            return expiryStatusJson;
    }

    return expiryStatusJson as UnifiedGroupExpiryStatus;
}

const retentionAction = (type: Schema.RetentionAction | null): RetentionActionType | undefined => {
    switch (type) {
        case 'DeleteAndAllowRecovery':
            return RetentionActionType.DeleteAndAllowRecovery;
        case 'MarkAsPastRetentionLimit':
            return RetentionActionType.MarkAsPastRetentionLimit;
        case 'MoveToArchive':
            return RetentionActionType.MoveToArchive;
        case 'MoveToDeletedItems':
            return RetentionActionType.MoveToDeletedItems;
        case 'MoveToFolder':
            return RetentionActionType.MoveToFolder;
        case 'None':
            return RetentionActionType.None;
        case 'PermanentlyDelete':
            return RetentionActionType.PermanentlyDelete;
        default:
            return undefined;
    }
};

const elcFolderType = (type: Schema.ElcFolderType | null): ElcFolderType | undefined => {
    switch (type) {
        case 'Calendar':
            return ElcFolderType.Calendar;
        case 'Contacts':
            return ElcFolderType.Contacts;
        case 'DeletedItems':
            return ElcFolderType.DeletedItems;
        case 'Drafts':
            return ElcFolderType.Drafts;
        case 'Inbox':
            return ElcFolderType.Inbox;
        case 'JunkEmail':
            return ElcFolderType.JunkEmail;
        case 'Journal':
            return ElcFolderType.Journal;
        case 'Notes':
            return ElcFolderType.Notes;
        case 'Outbox':
            return ElcFolderType.Outbox;
        case 'SentItems':
            return ElcFolderType.SentItems;
        case 'Tasks':
            return ElcFolderType.Tasks;
        case 'All':
            return ElcFolderType.All;
        case 'ManagedCustomFolder':
            return ElcFolderType.ManagedCustomFolder;
        case 'RssSubscriptions':
            return ElcFolderType.RssSubscriptions;
        case 'SyncIssues':
            return ElcFolderType.SyncIssues;
        case 'ConversationHistory':
            return ElcFolderType.ConversationHistory;
        case 'Personal':
            return ElcFolderType.Personal;
        case 'RecoverableItems':
            return ElcFolderType.RecoverableItems;
        case 'NonIpmRoot':
            return ElcFolderType.NonIpmRoot;
        default:
            return undefined;
    }
};

const convertRetentionPolicyTagToJson = (
    policyTag: Schema.RetentionPolicyTag
): RetentionPolicyTag => {
    return {
        ...policyTag,
        Type: elcFolderType(policyTag.Type),
        RetentionAction: retentionAction(policyTag.RetentionAction),
    } as RetentionPolicyTag;
};

const convertRetentionPolicyTagsToJson = (
    policyTags?: Schema.RetentionPolicyTag[] | null
): RetentionPolicyTag[] | null => {
    return policyTags ? policyTags.map(convertRetentionPolicyTagToJson) : null;
};

function getMailboxSettings(
    mailboxSettings: Schema.UnifiedGroupMailboxSettings | undefined
): UnifiedGroupMailboxSettings {
    return {
        ...mailboxSettings,
        RetentionPolicyTags: convertRetentionPolicyTagsToJson(mailboxSettings?.RetentionPolicyTags),
    } as UnifiedGroupMailboxSettings;
}

const convertDl2GroupMailCopyStatusToJson = (
    type: Schema.Dl2GroupMailCopyStatus | null
): Dl2GroupMailCopyStatus | undefined => {
    switch (type) {
        case 'None':
            return Dl2GroupMailCopyStatus.None;
        case 'InProgress':
            return Dl2GroupMailCopyStatus.InProgress;
        case 'Completed':
            return Dl2GroupMailCopyStatus.Completed;
        case 'Failed':
            return Dl2GroupMailCopyStatus.Failed;
        case 'Unknown':
            return Dl2GroupMailCopyStatus.Unknown;
        default:
            return undefined;
    }
};

function getAdditionalProperties(
    additionalProperties: Schema.UnifiedGroupDetailsAdditionalProperties
): UnifiedGroupDetailsAdditionalProperties {
    return {
        ...additionalProperties,
        Dl2GroupMailCopyStatus: convertDl2GroupMailCopyStatusToJson(
            additionalProperties?.Dl2GroupMailCopyStatus
        ),
    } as UnifiedGroupDetailsAdditionalProperties;
}

export default function parseGroupDetailsGqlResponse(
    gqlGroupDetailsResponse: Schema.UnifiedGroupDetails
): UnifiedGroupDetails {
    return {
        ...gqlGroupDetailsResponse,
        UserGroupRelationship: getUserGroupRelationship(
            gqlGroupDetailsResponse?.UserGroupRelationship
        ),
        MailboxSettings: getMailboxSettings(gqlGroupDetailsResponse?.MailboxSettings),
        AdditionalProperties: getAdditionalProperties(
            gqlGroupDetailsResponse?.AdditionalProperties
        ),
        UnifiedGroupSKU: mapUnifiedGroupSKU(gqlGroupDetailsResponse?.UnifiedGroupSKU),
        AccessType: mapAccessType(gqlGroupDetailsResponse?.AccessType),
        ExpiryStatus: mapExpiryStatus(gqlGroupDetailsResponse?.ExpiryStatus),
    } as UnifiedGroupDetails;
}
